import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCheck, Mic, Languages, Sparkles } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const SCRIPT = [
  { side: "in", ar: "السلام عليكم، عندكم شقة غرفتين للإيجار؟", en: "Hello, do you have a 2-bedroom apartment for rent?" },
  { side: "in", voice: true, ar: "متى أقدر أشوف الشقة؟", en: "When can I view the apartment?" },
  { side: "out", ar: "نعم، متاحة! تقدر تشوفها غداً الساعة ٥ مساءً.", en: "Yes, it's available! You can view it tomorrow at 5 PM.", draft: true },
];

const TIMELINE = [
  { typing: "in", d: 1100 },
  { show: 0, d: 2400 },
  { typing: "in", d: 1100 },
  { show: 1, d: 2600 },
  { typing: "out", d: 1500 },
  { show: 2, d: 3400 },
  { reset: true, d: 900 },
];

function Waveform({ light }) {
  return (
    <span className="inline-flex items-center gap-[3px] h-4">
      {[6, 11, 8, 14, 9, 5, 12, 7].map((h, i) => (
        <span key={i} className={`w-[3px] rounded-full ${light ? "bg-white/70" : "bg-accent/70"}`} style={{ height: h, animation: `wave 1s ease-in-out ${i * 0.08}s infinite` }} />
      ))}
    </span>
  );
}

export function HeroDemo() {
  const { lang } = useI18n();
  const en = lang === "en";
  const [shown, setShown] = useState([]);
  const [typing, setTyping] = useState(null);
  const idx = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    const tick = () => {
      const ev = TIMELINE[idx.current % TIMELINE.length];
      if (ev.reset) { setShown([]); setTyping(null); }
      else if (ev.typing) { setTyping(ev.typing); }
      else if (ev.show !== undefined) { setTyping(null); setShown((s) => [...s, ev.show]); }
      idx.current += 1;
      timer.current = setTimeout(tick, ev.d);
    };
    timer.current = setTimeout(tick, 500);
    return () => clearTimeout(timer.current);
  }, []);

  return (
    <div className="relative">
      <div className="absolute -inset-4 bg-accent/10 rounded-[2.25rem] -rotate-2" />
      <div className="relative mx-auto w-full max-w-[380px] rounded-[2rem] border-8 border-primary/90 bg-primary/90 shadow-2xl overflow-hidden">
        {/* Chat header */}
        <div className="bg-primary text-white px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-white/20 grid place-items-center font-bold text-sm">أ</div>
          <div className="leading-tight">
            <div className="font-semibold text-sm">{en ? "Ahmed · Customer" : "أحمد · عميل"}</div>
            <div className="text-[11px] text-white/70 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-400" /> {en ? "online" : "متصل"}</div>
          </div>
          <span className="ms-auto inline-flex items-center gap-1 text-[10px] font-semibold bg-accent/90 px-2 py-1 rounded-full"><Languages size={11} /> {en ? "Live translate" : "ترجمة فورية"}</span>
        </div>

        {/* Messages */}
        <div className="chat-pattern px-3 py-4 h-[360px] flex flex-col justify-end gap-2.5 overflow-hidden">
          <AnimatePresence>
            {shown.map((i) => {
              const m = SCRIPT[i];
              const isIn = m.side === "in";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  className={`flex ${isIn ? "justify-start" : "justify-end"}`}
                >
                  <div className={`max-w-[82%] rounded-2xl px-3 py-2 shadow-sm ${isIn ? "bg-white rounded-ss-sm" : "bg-[#128C4A] text-white rounded-ee-sm"}`}>
                    {m.draft && (
                      <div className="flex items-center gap-1 text-[10px] font-semibold text-white/80 mb-1"><Sparkles size={10} /> {en ? "AI smart reply" : "رد ذكي"}</div>
                    )}
                    {m.voice ? (
                      <div className="flex items-center gap-2 py-0.5">
                        <Mic size={15} className={isIn ? "text-accent" : "text-white"} />
                        <Waveform light={!isIn} />
                        <span className={`text-[10px] ${isIn ? "text-muted-foreground" : "text-white/80"}`}>0:06</span>
                      </div>
                    ) : (
                      <div className="text-[14px] leading-snug" dir="rtl">{m.ar}</div>
                    )}
                    {/* translation line */}
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ delay: 0.5, duration: 0.35 }}
                      className={`mt-1.5 pt-1.5 border-t text-[12.5px] leading-snug ${isIn ? "border-black/10 text-accent" : "border-white/25 text-white/85"}`}
                    >
                      <span className="inline-flex items-center gap-1 opacity-70 text-[10px] me-1"><Languages size={10} />{m.voice ? (en ? "transcribed" : "مُفرّغ") : (en ? "translated" : "مُترجم")}</span>
                      {m.en}
                    </motion.div>
                    {!isIn && <div className="flex justify-end mt-0.5"><CheckCheck size={13} className="text-sky-200" /></div>}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {typing && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`flex ${typing === "in" ? "justify-start" : "justify-end"}`}>
              <div className={`rounded-2xl px-3 py-2.5 shadow-sm ${typing === "in" ? "bg-white" : "bg-[#128C4A]"}`}>
                <span className="flex items-center gap-1">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className={`h-1.5 w-1.5 rounded-full ${typing === "in" ? "bg-muted-foreground/50" : "bg-white/70"}`} style={{ animation: `mic-bob 0.9s ease-in-out ${d * 0.15}s infinite` }} />
                  ))}
                </span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
